// The current-state table, v2: grouped by team / person when asked, health
// and team tags per row, blocked-by shown as titles, click a row to drill in.
//
// v3 (2026-09-08 check-in, PJ): an initiative with sub-actions carries a ▸
// chevron; it opens the row into one line per action — its people, status and
// stated date — without leaving the table. The drill-down drawer is unchanged.
//
// 2026-10-05 report ("the dates and sub-actions and where it says in progress,
// done, open, I can't click those"): a sub-action row opens its initiative at
// Dates and sub-actions, the way the timeline and the mind map already did,
// and so do the Status and Date cells of an initiative row.

import { Fragment, useState, type MouseEvent } from 'react';
import type { InitiativeAction, InitiativeState, ScheduleHealth } from '../../services/roadmapReports';
import { fmtDay, fmtDayYear, parseDay } from './roadmapTimelineUtils';
import type { ModalSection } from './TimelineChart';
import {
  ACTION_STATUS_LABELS,
  actionBlockers,
  actionDateSpan,
  actionsChip,
  ALL_INITIATIVES_LABEL,
  liveActions,
  mandateAreaTitle,
  rowTeamsLine,
  STATUS_LABELS,
  teamBarStyle,
  type InitiativeGroup,
  type TeamColors,
  type ViewBy,
  ownerTeamOf,
} from './roadmapViewUtils';
import { HealthChip } from './HealthChip';
import tableStyles from '../RoadmapReportsPage.module.css';
import styles from './RoadmapV2.module.css';

/** How many people a row names before the rest fold into "+N". */
export const PEOPLE_CHIP_CAP = 1;

/**
 * The people on an initiative as chips, capped so a six-person row is not six
 * lines tall (the list ran to seven screens). The fold names everyone on hover
 * and the pop-up still lists them all.
 */
export function PeopleChips({ people }: { people: string[] }) {
  const shown = people.slice(0, PEOPLE_CHIP_CAP);
  const rest = people.slice(PEOPLE_CHIP_CAP);
  return (
    <>
      {shown.map((p) => <span key={p} className={styles.personTag}>{p}</span>)}
      {rest.length > 0 && (
        <span className={styles.personTag} title={rest.join(', ')} data-testid="people-more">+{rest.length}</span>
      )}
    </>
  );
}

/** Healths that replace the status word: the row says "Late", not "In progress" plus a second chip. */
const OFF_TRACK: ReadonlySet<ScheduleHealth> = new Set<ScheduleHealth>(['late', 'at_risk', 'stuck']);

function teamsTitle(i: InitiativeState): string {
  const by = i.teams_override?.set_by_email;
  const who = by ? `set by ${by} on the roadmap page (kept on later runs)` : 'tagged by Merlin from the meetings it read';
  const owner = ownerTeamOf(i);
  return owner ? `Owned by ${owner}; related teams have a stake but do not own it — ${who}` : `Nobody named as the owner yet — ${who}`;
}

/**
 * The Date cell says one thing once: the stated target as "Sep 30" (with the
 * year when it is not this year), "ongoing" for standing work, or a grey
 * "No date" (it used to say "No target" in one column and "needs a date" in
 * the next on 38 rows).
 */
export function TargetDate({ initiative: i }: { initiative: InitiativeState }) {
  const d = parseDay(i.target_date);
  if (d) {
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return <span title={i.target_date ?? undefined}>{sameYear ? fmtDay(d) : fmtDayYear(d)}</span>;
  }
  if (i.status === 'ongoing') return <span className={tableStyles.muted} title="Standing work with no end date; its sub-actions carry the dates">ongoing</span>;
  if (i.status === 'done' || i.status === 'dropped' || i.status === 'parked') return <span className={tableStyles.muted}>—</span>;
  return <span className={tableStyles.muted} title="Nobody has stated a completion date. Merlin asks the owner rather than guessing one">No date</span>;
}

interface Props {
  groups: InitiativeGroup[];
  all: InitiativeState[];
  teamColors: TeamColors;
  /** Open the initiative pop-up, optionally scrolled to a section; with an
   * action key, the pop-up scoped to that one sub-action. */
  onSelect: (key: string, section?: ModalSection, action?: string) => void;
  /** How the groups were made: under a team or mandate heading a row does
   * not repeat the heading's team (nor, by mandate, its area). */
  viewBy?: ViewBy;
}

const COLUMNS = 6;

interface ActionRowProps {
  action: InitiativeAction;
  siblings: InitiativeAction[];
  parentKey: string;
  parentTitle: string;
  today: string;
  onOpen: (key: string, section: ModalSection, action: string) => void;
}

function ActionRow({ action, siblings, parentKey, parentTitle, today, onOpen }: ActionRowProps) {
  const { start, end, endKind } = actionDateSpan(action);
  const late = end !== null && end < today && action.status !== 'done' && action.status !== 'dropped';
  const blockers = actionBlockers(action, siblings);
  return (
    <tr
      className={styles.actionRow}
      data-testid={`action-${parentKey}`}
      onClick={() => onOpen(parentKey, 'dates', action.key)}
      title={`Open this sub-action of ${parentTitle}`}
      data-action="roadmap_action_open"
      data-target-initiative-key={parentKey}
      data-target-action-key={action.key}
    >
      <td />
      <td>
        <div className={styles.actionTitle}>
          <span className={styles.actionArrow} aria-hidden="true">↳</span>
          {/* A real button, so the row is reachable by keyboard; its click bubbles to the row. */}
          <button type="button" className={styles.actionOpen}>{action.title}</button>
        </div>
        {action.note && <div className={styles.actionNote}>{action.note}</div>}
        {action.sources.length > 0 && (
          <div className={styles.actionNote} title="Evidence Merlin cited for this action">{action.sources.join(' · ')}</div>
        )}
      </td>
      <td>
        {action.people.length === 0 ? (
          <span className={tableStyles.muted}>—</span>
        ) : (
          action.people.map((p) => <span key={p} className={styles.personTag}>{p}</span>)
        )}
      </td>
      <td>
        <span className={`${styles.actionStatus} ${styles[`action_${action.status}`] ?? ''}`}>
          {ACTION_STATUS_LABELS[action.status] ?? action.status}
        </span>
      </td>
      <td>
        <span className={styles.actionDates} title="Stated start → stated end (completion date once done). A ? is a date nobody stated.">
          <span className={start ? undefined : styles.needsTarget}>{start ?? '?'}</span>
          <span className={styles.actionDatesArrow} aria-hidden="true">→</span>
          <span className={late ? styles.actionLate : end ? undefined : styles.needsTarget} title={late ? 'Stated end date has passed and the action is not done' : endKind === 'completed' ? 'Completed on' : undefined}>
            {end ?? '?'}
          </span>
        </span>
      </td>
      <td>
        {blockers.length === 0 ? (
          <span className={tableStyles.muted}>—</span>
        ) : (
          <span className={styles.waitingOn}>{blockers.join(', ')}</span>
        )}
      </td>
    </tr>
  );
}

export function InitiativeTable({ groups, all, teamColors, onSelect, viewBy = 'list' }: Props) {
  const byKey = new Map(all.map((i) => [i.initiative_key, i]));
  const shown = groups.reduce((n, g) => n + g.items.length, 0);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const today = new Date().toISOString().slice(0, 10);

  const toggle = (e: MouseEvent, key: string) => {
    e.stopPropagation(); // the row itself opens the drawer
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /** The Status and Date cells land on the dates section, not the top of the pop-up. */
  const openDates = (e: MouseEvent, key: string) => {
    e.stopPropagation(); // otherwise the row opens the summary as well
    onSelect(key, 'dates');
  };

  return (
    <section className={tableStyles.structPanel} aria-label="Roadmap — current state of all initiatives">
      <div className={tableStyles.structHead}>
        <h2>Initiatives — current state</h2>
        <span className={tableStyles.structMeta}>
          click a row for the details · ▸ shows its sub-actions, click one for its dates
        </span>
      </div>
      {shown === 0 && <div className={styles.muted}>No initiatives match these filters.</div>}
      {shown > 0 && (
        <div className={tableStyles.structTableWrap}>
          <table className={tableStyles.structTable}>
            <thead>
              <tr>
                <th className={styles.expandCol} aria-label="Expand" />
                <th>Initiative</th>
                <th>Who</th>
                <th>Status</th>
                <th>Date</th>
                <th>Waiting on</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.label}>
                  {/* One group still gets its heading unless it is the plain list:
                      "AI only, by team" is one AI group, not an unlabelled table. */}
                  {(groups.length > 1 || g.label !== ALL_INITIATIVES_LABEL) && (
                    <tr className={styles.groupHead} data-testid={`group-${g.label}`}>
                      <td colSpan={COLUMNS}>
                        {g.color && <span className={styles.dot} style={{ background: g.color }} />}
                        {g.label} <span className={styles.meta}>({g.items.length})</span>
                      </td>
                    </tr>
                  )}
                  {g.items.map((i) => {
                    const actions = liveActions(i.actions);
                    const isOpen = open.has(i.initiative_key);
                    const chip = actionsChip(i.actions);
                    const teamLine = rowTeamsLine(i, viewBy);
                    const area = viewBy === 'mandate' ? null : mandateAreaTitle(i);
                    return (
                      <Fragment key={`${g.label}:${i.initiative_key}`}>
                        <tr
                          className={`${tableStyles.stateRow} ${isOpen ? styles.stateRowOpen : ''}`}
                          onClick={() => onSelect(i.initiative_key)}
                          data-testid={`row-${i.initiative_key}`}
                        >
                          <td className={styles.expandCol}>
                            {actions.length > 0 && (
                              <button
                                type="button"
                                className={`${styles.expander} ${isOpen ? styles.expanderOpen : ''}`}
                                onClick={(e) => toggle(e, i.initiative_key)}
                                aria-expanded={isOpen}
                                aria-label={`${isOpen ? 'Hide' : 'Show'} ${actions.length} sub-action${actions.length === 1 ? '' : 's'} of ${i.title}`}
                                title={isOpen ? 'Hide sub-actions' : `Show ${actions.length} sub-action${actions.length === 1 ? '' : 's'}`}
                              >
                                {isOpen ? '▾' : '▸'}
                              </button>
                            )}
                          </td>
                          <td className={styles.titleCell} style={teamBarStyle(i.teams, teamColors)}>
                            <div className={tableStyles.initiativeTitle}>{i.title}</div>
                            {i.summary && <div className={tableStyles.initiativeSummary} title={i.summary}>{i.summary}</div>}
                            <div className={styles.rowMeta}>
                              {teamLine && (
                                <span title={teamsTitle(i)}>{teamLine}</span>
                              )}
                              {area && (
                                <span title="The owning team's mandate area this work is filed under" data-testid={`mandate-${i.initiative_key}`}>
                                  {area}
                                </span>
                              )}
                              {!chip.empty && (
                                <span title="Sub-actions Merlin heard for this initiative. ▸ shows them" data-testid={`actions-chip-${i.initiative_key}`}>
                                  {chip.label}
                                </span>
                              )}
                              {i.open_questions.length > 0 && (
                                <span title="Questions Merlin still needs answered on this initiative">
                                  {i.open_questions.length} open question{i.open_questions.length === 1 ? '' : 's'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            {i.people.length === 0 ? (
                              <span className={tableStyles.muted}>—</span>
                            ) : (
                              <PeopleChips people={i.owner && i.people.includes(i.owner) ? [i.owner, ...i.people.filter((p) => p !== i.owner)] : i.people} />
                            )}
                          </td>
                          <td
                            onClick={(e) => openDates(e, i.initiative_key)}
                            title="Open its dates and sub-actions"
                            data-testid={`status-${i.initiative_key}`}
                          >
                            {OFF_TRACK.has(i.health) ? (
                              <HealthChip health={i.health} initiative={i} />
                            ) : (
                              <span className={`${tableStyles.statusChip} ${tableStyles[`status_${i.status}`] ?? ''}`}>
                                {STATUS_LABELS[i.status] ?? i.status}
                              </span>
                            )}
                          </td>
                          <td
                            className={tableStyles.targetCell}
                            onClick={(e) => openDates(e, i.initiative_key)}
                            data-testid={`date-${i.initiative_key}`}
                          >
                            <TargetDate initiative={i} />
                          </td>
                          <td>
                            {i.depends_on.length === 0 && !i.external_blocker ? (
                              <span className={tableStyles.muted}>—</span>
                            ) : (
                              <span className={styles.waitingOn}>
                                {[...i.depends_on.map((d) => byKey.get(d)?.title ?? d), ...(i.external_blocker ? [i.external_blocker] : [])].join(', ')}
                              </span>
                            )}
                          </td>
                        </tr>
                        {isOpen && actions.map((a) => (
                          <ActionRow key={`${i.initiative_key}:${a.key}`} action={a} siblings={actions} parentKey={i.initiative_key} parentTitle={i.title} today={today} onOpen={onSelect} />
                        ))}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
